import React, { useEffect, useRef, useState } from 'react';
import { Satellite, Map as MapIcon, Circle as CircleIcon, Hexagon, Trash2 } from 'lucide-react';

const G_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

let _loader = null;
function _loadGmaps() {
  if (typeof window !== 'undefined' && window.google?.maps?.drawing) return Promise.resolve(window.google);
  if (_loader) return _loader;
  _loader = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${G_KEY}&libraries=places,drawing&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(s);
  });
  return _loader;
}

/**
 * Geofence drawing editor — supports both circle and polygon shapes.
 *
 * Props:
 *   type: 'circle' | 'polygon'
 *   value: { center_lat, center_lon, radius_meters, polygon_coordinates }
 *   onChange({ center_lat, center_lon, radius_meters, polygon_coordinates })
 *   height: px
 *
 * The user can:
 *   - Toggle satellite vs. road view
 *   - Clear the current shape and draw a fresh one
 *   - Drag circle center / resize radius
 *   - Drag polygon vertices or click the map to add more (then double-click to close)
 */
export default function GeofenceMapDrawer({ type, value, onChange, height = 360 }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const shapeRef = useRef(null);
  const drawingMgrRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [mapType, setMapType] = useState('hybrid');

  // Emit current shape coords back to parent.
  const emit = () => {
    const g = window.google;
    const sh = shapeRef.current;
    if (!sh || !g) return;
    if (type === 'circle') {
      const c = sh.getCenter();
      onChange?.({
        center_lat: c.lat(),
        center_lon: c.lng(),
        radius_meters: Math.round(sh.getRadius()),
      });
    } else {
      const path = sh.getPath().getArray().map(ll => ({ latitude: ll.lat(), longitude: ll.lng() }));
      onChange?.({ polygon_coordinates: path });
    }
  };

  const clearShape = () => {
    if (shapeRef.current) {
      shapeRef.current.setMap(null);
      shapeRef.current = null;
    }
    // Re-arm drawing manager
    drawingMgrRef.current?.setDrawingMode(
      type === 'circle' ? window.google.maps.drawing.OverlayType.CIRCLE
                        : window.google.maps.drawing.OverlayType.POLYGON
    );
    onChange?.(type === 'circle'
      ? { center_lat: null, center_lon: null, radius_meters: null }
      : { polygon_coordinates: null });
  };

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const google = await _loadGmaps();
        if (cancelled) return;

        const center = value?.center_lat && value?.center_lon
          ? { lat: value.center_lat, lng: value.center_lon }
          : (value?.polygon_coordinates?.[0]
              ? { lat: value.polygon_coordinates[0].latitude, lng: value.polygon_coordinates[0].longitude }
              : { lat: 11.0573, lng: 77.3401 });

        const map = new google.maps.Map(mapEl.current, {
          center, zoom: 18, mapTypeId: 'hybrid',
          mapTypeControl: false, streetViewControl: false, fullscreenControl: false, tilt: 0,
        });
        mapRef.current = map;

        // Seed with existing shape if provided
        if (type === 'circle' && value?.center_lat && value?.center_lon && value?.radius_meters) {
          const c = new google.maps.Circle({
            map, editable: true, draggable: true,
            center, radius: value.radius_meters,
            fillColor: '#2563eb', fillOpacity: 0.2, strokeColor: '#2563eb', strokeWeight: 2,
          });
          shapeRef.current = c;
          c.addListener('radius_changed', emit);
          c.addListener('center_changed', emit);
          google.maps.event.addListener(c, 'dragend', emit);
        } else if (type === 'polygon' && value?.polygon_coordinates?.length >= 3) {
          const path = value.polygon_coordinates.map(p => ({ lat: p.latitude, lng: p.longitude }));
          const p = new google.maps.Polygon({
            map, editable: true, draggable: true,
            paths: path,
            fillColor: '#2563eb', fillOpacity: 0.2, strokeColor: '#2563eb', strokeWeight: 2,
          });
          shapeRef.current = p;
          google.maps.event.addListener(p.getPath(), 'set_at', emit);
          google.maps.event.addListener(p.getPath(), 'insert_at', emit);
          google.maps.event.addListener(p.getPath(), 'remove_at', emit);
          google.maps.event.addListener(p, 'dragend', emit);
        }

        // Drawing manager (only armed if no shape yet)
        const dm = new google.maps.drawing.DrawingManager({
          drawingMode: shapeRef.current ? null
            : (type === 'circle' ? google.maps.drawing.OverlayType.CIRCLE
                                 : google.maps.drawing.OverlayType.POLYGON),
          drawingControl: false,
          circleOptions: {
            editable: true, draggable: true,
            fillColor: '#2563eb', fillOpacity: 0.2, strokeColor: '#2563eb', strokeWeight: 2,
          },
          polygonOptions: {
            editable: true, draggable: true,
            fillColor: '#2563eb', fillOpacity: 0.2, strokeColor: '#2563eb', strokeWeight: 2,
          },
        });
        dm.setMap(map);
        drawingMgrRef.current = dm;

        google.maps.event.addListener(dm, 'circlecomplete', (c) => {
          shapeRef.current = c;
          dm.setDrawingMode(null);
          c.addListener('radius_changed', emit);
          c.addListener('center_changed', emit);
          google.maps.event.addListener(c, 'dragend', emit);
          emit();
        });
        google.maps.event.addListener(dm, 'polygoncomplete', (p) => {
          shapeRef.current = p;
          dm.setDrawingMode(null);
          google.maps.event.addListener(p.getPath(), 'set_at', emit);
          google.maps.event.addListener(p.getPath(), 'insert_at', emit);
          google.maps.event.addListener(p.getPath(), 'remove_at', emit);
          google.maps.event.addListener(p, 'dragend', emit);
          emit();
        });

        setLoading(false);
      } catch (e) {
        setErr(e.message || 'Failed to load map');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const toggleMapType = () => {
    const next = mapType === 'hybrid' ? 'roadmap' : 'hybrid';
    setMapType(next);
    mapRef.current?.setMapTypeId(next);
  };

  if (!G_KEY) return <div className="p-4 bg-red-50 border border-red-200 rounded text-xs text-red-700">Google Maps key missing</div>;

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-gray-200" data-testid="gfence-drawer">
      <div ref={mapEl} style={{ width: '100%', height }} />
      {loading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-xs">Loading map…</div>}
      {err && <div className="absolute inset-0 bg-red-50 flex items-center justify-center text-xs text-red-700 px-4 text-center">{err}</div>}
      {!loading && !err && (
        <div className="absolute top-2 right-2 flex flex-col gap-1.5">
          <button
            type="button"
            onClick={toggleMapType}
            className="bg-white hover:bg-gray-100 px-2 py-1.5 rounded-md shadow text-[11px] font-semibold flex items-center gap-1"
            data-testid="gfence-toggle-satellite"
          >
            {mapType === 'hybrid' ? <MapIcon size={11} /> : <Satellite size={11} />}
            {mapType === 'hybrid' ? 'Map' : 'Satellite'}
          </button>
          <button
            type="button"
            onClick={clearShape}
            className="bg-white hover:bg-red-50 text-red-600 px-2 py-1.5 rounded-md shadow text-[11px] font-semibold flex items-center gap-1"
            data-testid="gfence-clear"
            title="Clear & redraw"
          ><Trash2 size={11} />Redraw</button>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-[10px] text-gray-700 flex items-center gap-1 shadow">
        {type === 'circle' ? <CircleIcon size={11} /> : <Hexagon size={11} />}
        {type === 'circle'
          ? 'Click to place center, drag edges to resize'
          : 'Click on map to add vertices (min 3), drag to adjust'}
      </div>
    </div>
  );
}
